/**
 * schedulerAdapter.js
 *
 * Normalizes scheduler API responses into the UI view-model used by
 * EchoScheduler.js. Lives outside the component so it can be unit-tested
 * independently of React.
 *
 * Contract: vendor responses may arrive as either
 *   a) a raw JSON array    → `[ {id, patient_name, ...}, … ]`
 *   b) an object wrapper   → `{ appointments: [...] }` or `{ items: [...] }`
 *   c) a daily-summary doc → `{ date, total, by_status, punch_list, … }`
 *
 * We also accept legacy field names (`start`, `patient.name`, etc.) for
 * backward-compat with the local backend that mirrors the vendor shape.
 */

/** Extract the array-of-appointments from a raw response (any of the 3 shapes). */
export const extractAppointmentArray = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  // Canonical vendor / AriaOne shape.
  if (Array.isArray(data.appointments)) return data.appointments;
  // Common alternate envelopes — different vendor builds + paginated
  // backends use these interchangeably. We accept any of them so a
  // shape drift on the wire doesn't silently collapse the grid to
  // OPEN-only (production bug, May 2026).
  if (Array.isArray(data.items))   return data.items;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.records)) return data.records;
  if (Array.isArray(data.list))    return data.list;
  if (Array.isArray(data.rows))    return data.rows;
  // Nested data envelope: `{ data: [...] }` or `{ data: { appointments: [...] } }`.
  if (Array.isArray(data.data))    return data.data;
  if (data.data && typeof data.data === 'object') {
    if (Array.isArray(data.data.appointments)) return data.data.appointments;
    if (Array.isArray(data.data.items))        return data.data.items;
    if (Array.isArray(data.data.results))      return data.data.results;
  }
  return [];
};

/** Extract the punch-list from a daily-summary response. */
export const extractPunchArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.punch_list)) return data.punch_list;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/** Format an ISO datetime → `9:30` (24h short). Null-safe. */
const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: false }).replace(/^0/, '');
};

/** Return `YYYY-MM-DD` slice of an ISO string or Date-parseable value. */
const toIsoDate = (iso) => {
  if (!iso) return '';
  const s = typeof iso === 'string' ? iso : '';
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') return s.slice(0, 10);
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

/**
 * Normalize one appointment record.
 *
 * Vendor fields (AriaOne spec §18 M9d):
 *   id, patient_name, start_datetime, end_datetime, status, visit_type,
 *   visit_type_color, is_walkin, entity_name, location_name
 * Legacy/local fields (from the mirrored backend):
 *   start, end, patient.name, visit_type_name, visit_type.name, provider_name,
 *   entity.name, start_label, no_show_risk, copay
 */
export const normalizeAppointment = (a) => {
  if (!a) return null;
  // Defensive: enriched payload nests `patient`, `provider`, `entity`,
  // `location`, `visit_type` as objects. Basic payload uses scalar
  // `patient_name` / `entity_id` / `location_id` / `visit_type` strings.
  // Treat every nested lookup as null-safe so a partial enriched response
  // (e.g. patient resolved but visit_type missing) never crashes the UI.
  const patient    = (a.patient    && typeof a.patient    === 'object') ? a.patient    : null;
  const entityObj  = (a.entity     && typeof a.entity     === 'object') ? a.entity     : null;
  const providerObj= (a.provider   && typeof a.provider   === 'object') ? a.provider   : null;
  const locationObj= (a.location   && typeof a.location   === 'object') ? a.location   : null;
  const visitObj   = (a.visit_type && typeof a.visit_type === 'object') ? a.visit_type : null;

  const startAt = a.start_datetime || a.start || null;
  const endAt   = a.end_datetime   || a.end   || null;
  // `visit_type` arrives as a string (vendor) OR an object {name, color} (enriched/legacy).
  const visitTypeStr =
    typeof a.visit_type === 'string' ? a.visit_type :
    (a.visit_type_name || (visitObj && visitObj.name) || a.type || null);
  const providerStr =
    a.provider_name
    || (providerObj && (providerObj.name || providerObj.full_name))
    || (entityObj && (entityObj.name || entityObj.full_name))
    || a.entity_name
    || (typeof a.provider === 'string' ? a.provider : null);
  return {
    id:        a.id || a._id || null,
    time:      a.start_label || formatTime(startAt),
    startAt,
    endAt,
    patient:     a.patient_name || (patient && patient.name) || (patient && patient.full_name) || null,
    patientName: a.patient_name || (patient && patient.name) || (patient && patient.full_name) || null,
    patientId:   a.patient_id || (patient && (patient.id || patient._id)) || null,
    patientPhone:a.patient_phone || (patient && patient.phone) || null,
    patientDob:  a.patient_dob   || (patient && patient.dob)   || null,
    type:      visitTypeStr,
    visitType: visitTypeStr,
    visitTypeId:  a.visit_type_id || (visitObj && (visitObj.id || visitObj._id)) || null,
    visitColor: a.visit_type_color || (visitObj && visitObj.color) || null,
    duration:   a.duration_minutes || (visitObj && visitObj.duration_minutes) || null,
    isWalkIn:  !!(a.is_walkin ?? a.isWalkIn),
    provider:    providerStr,
    providerId:  a.entity_id || a.provider_id || (entityObj && (entityObj.id || entityObj._id)) || (providerObj && (providerObj.id || providerObj._id)) || null,
    entityName:  a.entity_name || (entityObj && (entityObj.name || entityObj.full_name)) || null,
    locationId:   a.location_id || (locationObj && (locationObj.id || locationObj._id)) || null,
    locationName: a.location_name || (locationObj && (locationObj.name || locationObj.location_name)) || null,
    status:   a.status || 'scheduled',
    noShow:   a.no_show_risk ?? a.no_show_score ?? null,
    copay:    a.copay ?? (a.financials && a.financials.copay_due) ?? null,
    balance:  a.balance ?? (a.financials && a.financials.balance) ?? null,
    insuranceVerified: a.insurance_verified ?? (a.insurance && a.insurance.verified) ?? null,
    // Carry-forward: enriched-only fields that downstream surfaces
    // (rooming queue, waitlist, recall) may opt-in to render. UI never
    // crashes when these are absent.
    notes:     a.notes ?? null,
    flags:     Array.isArray(a.flags) ? a.flags : [],
  };
};

/** Normalize an array of appointments, filtered for same-day (when `date` provided). */
export const normalizeAppointments = (data, date) => {
  const raw = extractAppointmentArray(data);
  const mapped = raw.map(normalizeAppointment).filter(Boolean);
  if (!date) return mapped;
  // Defensive same-day filter — drops records whose ISO date portion doesn't
  // match `date` (YYYY-MM-DD). If `startAt` is absent, keep the record so we
  // never silently hide valid data.
  return mapped.filter((a) => !a.startAt || toIsoDate(a.startAt) === date);
};

/** Normalize one punch-list item. */
export const normalizePunchItem = (it, idx) => {
  if (!it) return null;
  return {
    id: it.id || `pl-${idx}`,
    priority: it.priority || 'blue',
    patient: it.patient || it.patient_name || null,
    time:    it.time || it.time_label || null,
    issue:   it.issue || it.message || '',
    financial: it.financial || it.financial_label || null,
    action:    it.action || null,
    actionType: it.action_type || it.actionType || null,
    echoHandling: it.echo_handling || it.echoHandling || null,
  };
};

export const normalizePunchList = (data) =>
  extractPunchArray(data).map(normalizePunchItem).filter(Boolean);

/** Extract the array-of-slots from a raw response. */
export const extractSlotArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.slots)) return data.slots;
  if (data && Array.isArray(data.available_slots)) return data.available_slots;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/** Normalize one slot record (vendor §17 M9c + legacy fallbacks). */
export const normalizeSlot = (s) => {
  if (!s) return null;
  const startAt = s.start_datetime || s.start || null;
  const endAt   = s.end_datetime   || s.end   || null;
  return {
    slotId:      s.id || s.slot_id || `slot-${startAt || Math.random()}`,
    startAt,
    endAt,
    time:        s.start_label || formatTime(startAt),
    providerId:   s.entity_id || s.provider_id || null,
    providerName: s.entity_name || s.provider_name || s.entity?.name || null,
    locationId:   s.location_id || s.location?.id || null,
    locationName: s.location_name || s.location?.name || null,
  };
};

export const normalizeSlots = (data, date) => {
  const mapped = extractSlotArray(data).map(normalizeSlot).filter(Boolean);
  if (!date) return mapped;
  return mapped.filter((s) => !s.startAt || toIsoDate(s.startAt) === date);
};

/**
 * Merge normalized slots + normalized appointments into a single Today-grid
 * view-model. The key for matching is `startAt|locationId|providerId`; when
 * two records collide, the appointment wins and the card is marked BOOKED.
 *
 * Booked appointments that don't line up with an inventory slot (e.g. walk-
 * ins or overbooks) are surfaced as standalone BOOKED cards so they still
 * appear on the grid.
 *
 * @param {Array} slots
 * @param {Array} appointments
 * @param {Object} [filters] `{providerId, locationId}` — drops rows that
 *   don't match. `locationId = null` or `undefined` means "all".
 */
/**
 * normalizeStatus(raw) → canonical UI status token.
 *
 * Vendor emits a wide variety of strings for in-progress appointments
 * (`checked_in`, `arrived`, `in_exam`, `in_room`, `with_provider`,
 * `ready_checkout`, `checkout`, `completed`, `finished`, `no_show`,
 * `noshow`, `cancelled`, `canceled`, …). The UI grid renders 7
 * canonical states. Anything else passes through verbatim so a brand-
 * new vendor token surfaces in DOM rather than silently collapsing to
 * "booked".
 */
export const normalizeStatus = (raw) => {
  const v = (raw == null ? '' : String(raw)).trim().toLowerCase();
  if (!v) return 'booked';
  switch (v) {
    case 'scheduled':
    case 'booked':
    case 'confirmed':
      return 'booked';
    case 'checked_in':
    case 'checked-in':
    case 'arrived':
    case 'waiting':
      return 'checked_in';
    case 'in_exam':
    case 'in-exam':
    case 'in_room':
    case 'in-room':
    case 'with_provider':
    case 'with-provider':
    case 'roomed':
      return 'in_exam';
    case 'ready_checkout':
    case 'ready-checkout':
    case 'ready_for_checkout':
    case 'checkout':
    case 'ready_to_checkout':
      return 'checkout';
    case 'completed':
    case 'finished':
    case 'done':
    case 'closed':
      return 'completed';
    case 'no_show':
    case 'no-show':
    case 'noshow':
      return 'no_show';
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    default:
      return v;  // passthrough for unknown vendor tokens
  }
};

export const mergeSlotsAndAppointments = (slots, appointments, filters = {}) => {
  const { providerId, locationId } = filters;
  // Composite match: time + location + provider. `startAt` is
  // normalized to second-precision UTC ISO so vendor variations
  // (millis, +00:00 vs Z) don't break the join.
  const normTime = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
  };
  const keyOf = (r) => [normTime(r.startAt), r.locationId || '', r.providerId || ''].join('|');
  // Looser fallback key — used when the strict 3-tuple doesn't match.
  // Some vendor pre-2026-05 payloads return slot.locationId from the
  // by-location shard wrapper while the appointment record carries it
  // on a nested `location` object. The relaxed key matches by
  // (time + provider) and prefers same-location appointments first.
  const loosKey = (r) => [normTime(r.startAt), r.providerId || ''].join('|');

  // Vendor §18: cancelled appointments must NOT appear on the primary
  // active grid. They are tracked separately in `summary.cancelled`
  // so a future "show cancelled" toggle can opt them back in without
  // re-doing this merge. Filter happens up-front so cancelled entries
  // cannot accidentally consume an open slot via the strict-key match.
  const cancelledAppts = [];
  const activeAppts = [];
  for (const a of appointments || []) {
    if (normalizeStatus(a?.status) === 'cancelled') {
      cancelledAppts.push(a);
    } else {
      activeAppts.push(a);
    }
  }

  const apptByKey = new Map();
  const apptByLooseKey = new Map();
  for (const a of activeAppts) {
    apptByKey.set(keyOf(a), a);
    // For loose key, keep the LIST so we can disambiguate ties.
    const lk = loosKey(a);
    if (!apptByLooseKey.has(lk)) apptByLooseKey.set(lk, []);
    apptByLooseKey.get(lk).push(a);
  }

  const matches = (r) => {
    if (providerId && r.providerId && r.providerId !== providerId) return false;
    if (locationId && r.locationId && r.locationId !== locationId) return false;
    return true;
  };

  const cards = [];
  const consumedAppts = new Set();

  // Build a (locationId → locationName) lookup from the slot inventory
  // so appointment-only cards (where the basic /appointments payload
  // omits `location_name`) can still surface a human-readable label.
  const locNameByLocId = new Map();
  for (const s of slots || []) {
    if (s?.locationId && s.locationName && !locNameByLocId.has(s.locationId)) {
      locNameByLocId.set(s.locationId, s.locationName);
    }
  }

  for (const s of slots || []) {
    if (!matches(s)) continue;
    const k = keyOf(s);
    let a = apptByKey.get(k);
    if (!a) {
      // Relaxed fallback for stragglers where the strict 3-tuple
      // doesn't match (e.g. appointment.locationId arrived empty from
      // an older vendor payload). Only matches when EITHER the slot
      // or the appointment lacks a locationId — never crosses two
      // populated locations, otherwise we'd mis-paint the wrong shard.
      const candidates = apptByLooseKey.get(loosKey(s)) || [];
      a = candidates.find((c) => !c.locationId || !s.locationId) || null;
    }
    if (a) {
      // Track consumed using BOTH keys so the walk-in pass below
      // doesn't double-render an already-consumed appointment.
      consumedAppts.add(keyOf(a));
      cards.push({
        kind: 'booked',
        status: normalizeStatus(a.status),
        rawStatus: a.status || null,
        startAt: s.startAt, time: s.time,
        providerId: s.providerId, providerName: s.providerName,
        locationId: s.locationId, locationName: s.locationName,
        patient: a.patient || a.patientName || null,
        patientId: a.patientId || null,
        visitType: a.visitType || a.type || null,
        visitColor: a.visitColor || null,
        isWalkIn: !!a.isWalkIn,
        // Richer per-card fields used by the slot card (parity lift):
        //   copay / balance      → amber-tinted financial chip
        //   noShow               → red-tinted risk pill above ~30%
        //   insuranceVerified    → unverified warning pill when false
        noShow:   a.noShow ?? null,
        copay:    a.copay ?? null,
        balance:  a.balance ?? null,
        insuranceVerified: a.insuranceVerified,
        appointmentId: a.id || null,
        slotId: s.slotId,
      });
    } else {
      cards.push({
        kind: 'open',
        status: 'open',
        startAt: s.startAt, time: s.time,
        providerId: s.providerId, providerName: s.providerName,
        locationId: s.locationId, locationName: s.locationName,
        slotId: s.slotId,
      });
    }
  }

  // Walk-ins / overbooked / appointment-only times (no matching slot).
  // This is the critical "appointment-only card" path — when the vendor
  // returns a 13:00 in_exam at Main Office and our slot inventory has
  // NO 13:00 Main slot (or the appointment lands on a 14:10 off-grid
  // time), the merge MUST still surface the card so the provider sees
  // their actual day.
  for (const a of activeAppts) {
    const k = keyOf(a);
    if (consumedAppts.has(k)) continue;
    if (!matches(a)) continue;
    // Basic /appointments payload often omits `location_name`. Resolve
    // it from the slot inventory's (locationId → name) map so every
    // card on the grid carries a human-readable location label.
    const resolvedLocName = a.locationName
      || (a.locationId && locNameByLocId.get(a.locationId))
      || null;
    cards.push({
      kind: 'booked',
      status: normalizeStatus(a.status),
      rawStatus: a.status || null,
      startAt: a.startAt, time: a.time,
      providerId: a.providerId, providerName: a.provider || a.providerName || a.entityName,
      locationId: a.locationId, locationName: resolvedLocName,
      patient: a.patient || a.patientName,
      patientId: a.patientId || null,
      visitType: a.visitType || a.type,
      visitColor: a.visitColor,
      isWalkIn: !!a.isWalkIn,
      noShow:   a.noShow ?? null,
      copay:    a.copay ?? null,
      balance:  a.balance ?? null,
      insuranceVerified: a.insuranceVerified,
      appointmentId: a.id,
      slotId: null,
    });
  }

  // Sort by startAt then locationName so the grid reads top-to-bottom by time.
  cards.sort((a, b) => {
    if (a.startAt && b.startAt && a.startAt !== b.startAt) return a.startAt < b.startAt ? -1 : 1;
    return (a.locationName || '').localeCompare(b.locationName || '');
  });

  // Summary now tracks the in-progress states separately for the
  // header counters and for the debug guard in EchoScheduler.
  const summary = {
    open:       cards.filter((c) => c.kind === 'open').length,
    booked:     cards.filter((c) => c.kind === 'booked').length,
    inProgress: cards.filter((c) => c.kind === 'booked'
                                  && (c.status === 'checked_in'
                                   || c.status === 'in_exam'
                                   || c.status === 'checkout')).length,
    completed:  cards.filter((c) => c.kind === 'booked' && c.status === 'completed').length,
    noShow:     cards.filter((c) => c.kind === 'booked' && c.status === 'no_show').length,
    // Cancelled appointments are hidden from the active grid but
    // counted here so a future "show cancelled" toggle has the number
    // ready without needing to re-run the merge.
    cancelled:  cancelledAppts.length,
    total:      cards.length,
  };
  return { cards, summary };
};

// Re-export helpers for tests.
export const _internal = { formatTime, toIsoDate };
